import React, {
  useState,
  ChangeEvent,
  KeyboardEvent,
  FormEvent,
  useRef,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom"; // Import navigation hook
import "../style/css/EditProfile.css";
import api from "../api.js";

// 2. Updated Interface: Removed website, added experience_years
interface ProfileData {
  name: string;
  username: string;
  title: string;
  avatarUrl?: string;
  experience_years: string; // Using string for input, will parse to number if needed
  bio: string;
  specialties: string[];
  profilePicture?: string; // To store the URL from backend
}

const EditProfile: React.FC = () => {
  const navigate = useNavigate(); // Initialize navigation

  // State for form fields
  const [formData, setFormData] = useState<ProfileData>({
    name: "",
    username: "",
    title: "",
    experience_years: "",
    bio: "",
    avatarUrl: "",
    specialties: [],
  });

  // State for the selected file and preview
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // State for UI feedback (Error box instead of alert)
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Refs
  const specialtyInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null); // 4. Ref for hidden file input

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get("/profiles/");
        const data: ProfileData = response.data;

        // Populate form
        setFormData({
          ...data,
          experience_years: data.experience_years || "",
        });

        // Set initial preview if user has a photo
        if (data.profilePicture) {
          setPreviewUrl(data.profilePicture);
        }
      } catch (err) {
        setErrorMessage("Error al cargar la información del perfil.");
      }
    };

    fetchProfile();
  }, []);

  // State for the temporary specialty input
  const [specialtyInput, setSpecialtyInput] = useState<string>("");

  // Handle text inputs
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 4. Handle Image Selection
  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      // Create a local preview URL so the user sees the change immediately
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Trigger the hidden file input
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle specialties (Same as before)
  const handleSpecialtyKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = specialtyInput.trim();
      if (trimmedValue && !formData.specialties.includes(trimmedValue)) {
        setFormData((prev) => ({
          ...prev,
          specialties: [...prev.specialties, trimmedValue],
        }));
        setSpecialtyInput("");
      }
    }
  };

  const removeSpecialty = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      specialties: prev.specialties.filter((tag) => tag !== tagToRemove),
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    // 5. Create FormData for Multipart upload (Required for Cloudinary/Files)
    const submitData = new FormData();

    submitData.append("name", formData.name);
    submitData.append("username", formData.username);
    submitData.append("title", formData.title);
    submitData.append("bio", formData.bio);
    submitData.append("experience_years", formData.experience_years);
    formData.specialties.forEach((specialty) => {
      submitData.append("specialties", specialty);
    });
    // submitData.append("specialties", formData.specialties);

    // Note: Your backend serializer marks 'specialties' as read_only=True.
    // If you need to update them, your backend logic needs to handle list input manually.
    // Generally, FormData doesn't send arrays nicely unless backend expects multiple keys.
    // We will skip appending specialties here unless you adjust the backend to accept them via a specific key.

    // Append the image if a new one was selected
    if (selectedFile) {
      submitData.append("image_upload", selectedFile);
    }

    try {
      // Using POST based on your code, but 'edit' usually uses PUT or PATCH.
      // Ensure your backend route /profiles/ accepts POST for updates.
      await api.post("/profiles/", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // 3. Navigate on success instead of alert
      navigate("/profile");
    } catch (err) {
      // 1. Set error message state to show the box
      setErrorMessage(
        "Error al guardar los cambios. Por favor, verifica los datos e intenta de nuevo.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section>
      <div className="container">
        <div className="text-center">
          <h1>Editar Perfil</h1>
          <p>Actualiza tu información personal y profesional</p>
        </div>

        <div className="profile-card">
          {/* 1. Styled Error Box */}
          {errorMessage && (
            <div className="error-message-box">
              <span style={{ marginRight: "10px" }}>⚠️</span>
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Avatar Section */}
            {/* Avatar Section */}
            <div className="avatar-section">
              <img
                // Show the preview URL if exists (from file selection), otherwise existing avatar, then placeholder
                src={
                  previewUrl || formData.avatarUrl || "/user_placeholder.svg"
                }
                alt="Avatar Preview"
                className="avatar-preview"
                style={{ objectFit: "cover" }}
              />
              <div>
                <h4 style={{ marginBottom: "5px" }}>Foto de perfil</h4>
                <p style={{ fontSize: "0.9rem", marginBottom: "10px" }}>
                  Recomendado 300x300 px.
                </p>

                {/* 4. Hidden Input + Button Trigger */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                  accept="image/png, image/jpeg, image/jpg, image/gif"
                />
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleUploadClick}
                >
                  Cambiar foto
                </button>
              </div>
            </div>

            {/* Main Info Grid */}
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Nombre completo</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  placeholder="Ej. Dra. María González"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="username">Nombre de usuario</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  className="form-input"
                  placeholder="@nutri.maria"
                  value={formData.username}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="title">Título Profesional</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-input"
                  placeholder="Ej. Nutricionista Clínico"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>

              {/* 2. Years of Experience Field */}
              <div className="form-group">
                <label htmlFor="experience_years">Años de Experiencia</label>
                <input
                  type="number"
                  id="experience_years"
                  name="experience_years"
                  className="form-input"
                  placeholder="Ej. 5"
                  min="0"
                  value={formData.experience_years}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Biography */}
            <div className="form-group">
              <label htmlFor="bio">Biografía</label>
              <textarea
                id="bio"
                name="bio"
                rows={4}
                className="form-input"
                placeholder="Describe tu experiencia..."
                value={formData.bio}
                onChange={handleInputChange}
              />
            </div>

            {/* Specialties */}
            <div
              className="form-group"
              style={{ marginBottom: "var(--space-lg)" }}
            >
              <label htmlFor="specialties">Especialidades</label>
              <div
                className="specialties-wrapper"
                onClick={() => specialtyInputRef.current?.focus()}
              >
                {formData.specialties.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <span
                      className="tag-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSpecialty(tag);
                      }}
                    >
                      ×
                    </span>
                  </span>
                ))}

                <input
                  ref={specialtyInputRef}
                  type="text"
                  className="specialties-input"
                  placeholder={
                    formData.specialties.length === 0
                      ? "Ej. Nutrición deportiva, Pediatría..."
                      : ""
                  }
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  onKeyDown={handleSpecialtyKeyDown}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => navigate("/profile")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={isLoading}
              >
                {isLoading ? "Guardando..." : "Guardar Cambios"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default EditProfile;
